import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LadderIconKey } from './LadderIcons';
import { playTick, playWin } from 'common/utils/sfx';
import styles from './LadderBoard.module.css';
import { seededRandom } from 'common/utils/seededRandom';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

/**
 * 🪜 사다리 보드 — 알고리즘 + SVG 렌더링 + 경로 애니메이션
 *
 * 구성:
 *  - N 개 수직선 (names.length == results.length == N)
 *  - 랜덤 가로선 배치 (row x col, 인접하지 않게)
 *  - 각 시작 열에서 경로 추적 → 끝 열 결과 매핑
 *  - revealSeed 변경 시 경로가 2.5초 동안 그려짐
 */

export interface LadderBoardProps {
    names: string[];
    results: Array<{ text: string; icon: LadderIconKey }>;
    seed: number;
    animating: boolean;
    onFinish?: (mapping: Array<{ startIdx: number; endIdx: number }>) => void;
    height?: number;
}

const ROW_HEIGHT = 26;
const COL_SPACING = 64;
const PADDING_X = 32;
const TOP_LABEL_H = 40;
const BOTTOM_LABEL_H = 44;


function generateHorizontals(n: number, rows: number, seed: number): Array<{ row: number; col: number }> {
    if (n < 2) return [];
    const rand = seededRandom(seed);
    const lines: Array<{ row: number; col: number }> = [];
    for (let r = 0; r < rows; r++) {
        const used = new Set<number>();
        for (let c = 0; c < n - 1; c++) {
            if (used.has(c - 1)) continue; // 인접 금지
            if (rand() < 0.42) {
                lines.push({ row: r, col: c });
                used.add(c);
            }
        }
    }
    return lines;
}

function traceColumn(startCol: number, lines: Array<{ row: number; col: number }>, rows: number): number[] {
    const path: number[] = [startCol];
    let col = startCol;
    for (let r = 0; r < rows; r++) {
        const leftLine = lines.find(l => l.row === r && l.col === col - 1);
        const rightLine = lines.find(l => l.row === r && l.col === col);
        if (leftLine) col--;
        else if (rightLine) col++;
        path.push(col);
    }
    return path;
}

function colX(col: number): number { return PADDING_X + col * COL_SPACING; }
function rowY(row: number): number { return TOP_LABEL_H + row * ROW_HEIGHT; }

const PALETTE = ['#EB5757', '#F2994A', '#F2C94C', '#6FCF97', '#56CCF2', '#2F80ED', '#BB6BD9', '#EF7C9F', '#27AE60', '#8B5A3C'];

const LadderBoard: React.FC<LadderBoardProps> = ({ names, results, seed, animating, onFinish, height = 14 }) => {
    const n = Math.min(names.length, results.length);
    const rows = height;
    const svgRef = useRef<SVGSVGElement>(null);
    const [animProgress, setAnimProgress] = useState(0); // 0~1

    const { horizontals, paths } = useMemo(() => {
        const h = generateHorizontals(n, rows, seed);
        const p: number[][] = [];
        for (let i = 0; i < n; i++) p.push(traceColumn(i, h, rows));
        return { horizontals: h, paths: p };
    }, [n, rows, seed]);

    // 각 참여자 경로의 실제 좌표 (polyline points)
    const pathPoints = useMemo(() => {
        return paths.map((cols) => {
            const pts: Array<[number, number]> = [];
            pts.push([colX(cols[0]), TOP_LABEL_H]);
            for (let r = 0; r < rows; r++) {
                const curCol = cols[r];
                const nextCol = cols[r + 1];
                if (nextCol !== curCol) {
                    const yMid = rowY(r) + ROW_HEIGHT / 2;
                    pts.push([colX(curCol), yMid]);
                    pts.push([colX(nextCol), yMid]);
                }
                pts.push([colX(nextCol), rowY(r + 1)]);
            }
            return pts;
        });
    }, [paths, rows]);

    // 경로 길이 계산
    const pathLengths = useMemo(() => pathPoints.map(pts => {
        let len = 0;
        for (let i = 1; i < pts.length; i++) {
            const dx = pts[i][0] - pts[i - 1][0];
            const dy = pts[i][1] - pts[i - 1][1];
            len += Math.sqrt(dx * dx + dy * dy);
        }
        return len;
    }), [pathPoints]);

    // 애니메이션
    useEffect(() => {
        if (!animating) { setAnimProgress(0); return; }
        const DURATION = 2500;
        const start = Date.now();
        let raf = 0;
        let lastTickProgress = 0;
        const tick = () => {
            const elapsed = Date.now() - start;
            const p = Math.min(1, elapsed / DURATION);
            setAnimProgress(p);
            // 10% 마다 딸깍
            if (p - lastTickProgress >= 0.1) {
                lastTickProgress = p;
                playTick();
            }
            if (p < 1) raf = requestAnimationFrame(tick);
            else {
                playWin();
                if (onFinish) onFinish(paths.map((cols, i) => ({ startIdx: i, endIdx: cols[cols.length - 1] })));
            }
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [animating, seed]);

    const svgW = PADDING_X * 2 + (n - 1) * COL_SPACING;
    const svgH = TOP_LABEL_H + rows * ROW_HEIGHT + BOTTOM_LABEL_H;

    if (n < 2) return <div className={cls("ladder-empty")}>참여자 2명 이상 필요</div>;

    return (
        <svg ref={svgRef} viewBox={`0 0 ${svgW} ${svgH}`} width="100%" className={cls("ladder-svg")} preserveAspectRatio="xMidYMid meet">
            {/* 상단 이름 */}
            {names.map((name, i) => (
                <g key={`n-${i}`}>
                    <rect x={colX(i) - 26} y={6} width={52} height={26} rx={13}
                        fill={PALETTE[i % PALETTE.length]} opacity={0.9} />
                    <text x={colX(i)} y={24} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="700">
                        {name.length > 5 ? name.slice(0, 4) + '…' : name}
                    </text>
                </g>
            ))}

            {/* 수직선 */}
            {Array.from({ length: n }).map((_, i) => (
                <line key={`v-${i}`} x1={colX(i)} y1={TOP_LABEL_H} x2={colX(i)} y2={rowY(rows)}
                    stroke="#d0d0d0" strokeWidth={2} />
            ))}

            {/* 가로선 */}
            {horizontals.map((l, i) => (
                <line key={`h-${i}`}
                    x1={colX(l.col)} y1={rowY(l.row) + ROW_HEIGHT / 2}
                    x2={colX(l.col + 1)} y2={rowY(l.row) + ROW_HEIGHT / 2}
                    stroke="#a0a0a0" strokeWidth={2.5} strokeLinecap="round" />
            ))}

            {/* 참여자 경로 (애니메이션) */}
            {animating && pathPoints.map((pts, i) => {
                const pointsStr = pts.map(p => `${p[0]},${p[1]}`).join(' ');
                const len = pathLengths[i];
                const drawn = len * animProgress;
                const dash = `${drawn} ${Math.max(0, len - drawn)}`;
                return (
                    <polyline key={`p-${i}`}
                        points={pointsStr}
                        fill="none"
                        stroke={PALETTE[i % PALETTE.length]}
                        strokeWidth={3}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeDasharray={dash}
                        opacity={0.92}
                    />
                );
            })}

            {/* 하단 결과 — SVG 내부 카드에 색상별 텍스트 (아이콘은 결과 리스트에서 표시) */}
            {results.map((res, i) => (
                <g key={`r-${i}`}>
                    <rect x={colX(i) - 32} y={rowY(rows) + 8} width={64} height={30} rx={8}
                        fill="#fff" stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} />
                    <text x={colX(i)} y={rowY(rows) + 28} textAnchor="middle" fill="#333" fontSize={11} fontWeight="700">
                        {res.text.length > 7 ? res.text.slice(0, 6) + '…' : res.text}
                    </text>
                </g>
            ))}
        </svg>
    );
};

export default LadderBoard;
